import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MangaReader from "./components/MangaReader";
import NewsReader from "./components/NewsReader";
import { mangaList } from "@/mocks/manga";
import { overflowChapters, chapterTitlesByManga } from "@/mocks/chapters";
import { newsArticles } from "@/mocks/content";
import type { Chapter } from "@/mocks/chapters";

export default function ArticlePage() {
  const { slug } = useParams();
  const manga = mangaList.find((m) => m.slug === slug);
  const news = newsArticles.find((a) => a.slug === slug);

  const [chapterIdx, setChapterIdx] = useState(0);
  const selectChapter = (next: number) => {
    setChapterIdx(next);
    const el = document.getElementById("reader");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (manga) {
    const chapterList: Chapter[] = chapterTitlesByManga[manga.id] ?? overflowChapters;
    const related = mangaList.filter((m) => m.id !== manga.id).slice(0, 5);
    const jsonLd = [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: `${manga.title} 漫画-免费阅读全集中文故事`,
        url: `https://swimchats.github.io/article/${manga.slug}`,
        image: manga.cover,
        description: manga.synopsis,
        author: { "@type": "Organization", name: "178漫画网" },
        inLanguage: "zh-CN",
        isPartOf: { "@type": "WebSite", name: "178漫画网", url: "https://swimchats.github.io/" },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://swimchats.github.io/" },
          { "@type": "ListItem", position: 2, name: `${manga.categoryName}漫画`, item: `https://swimchats.github.io/category/${manga.category}` },
          { "@type": "ListItem", position: 3, name: manga.title },
        ],
      },
    ];

    return (
      <div className="min-h-screen bg-background-50">
        <Seo
          title={`${manga.title}漫画-免费阅读全集中文故事-178漫画网`}
          description={`${manga.synopsis} 在178漫画网免费阅读${manga.title}漫画全集，在线浏览全集中文故事，无需付费即可享受完整章节与高清画面。${manga.title}漫画,免费阅读,漫画全集,178漫画网。`}
          keywords={`${manga.title}漫画,免费阅读,漫画全集,178漫画网`}
          canonical={`https://swimchats.github.io/article/${manga.slug}`}
          ogImage={manga.cover}
          jsonLd={jsonLd}
        />
        <Navbar />
        <main className="mx-auto max-w-7xl px-4 md:px-6 pt-24 md:pt-28 pb-14">
          <div className="pt-4">
            <Breadcrumb
              items={[
                { label: `${manga.categoryName}漫画`, to: `/category/${manga.category}` },
                { label: manga.title },
              ]}
            />
          </div>

          <header className="mt-6 flex flex-col gap-6 rounded-lg border border-background-200 bg-background-100 p-4 md:p-6 sm:flex-row">
            <div className="h-64 w-full shrink-0 overflow-hidden rounded-lg bg-background-100 sm:h-72 sm:w-52">
              <img src={manga.cover} alt={`${manga.title} 漫画封面`} title={`${manga.title} 漫画 免费阅读`} className="h-full w-full object-cover object-top" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">{manga.title}</h1>
                <span className="rounded-md bg-accent-100 px-2 py-1 text-xs font-medium text-accent-700">{manga.status}</span>
              </div>
              <p className="mt-1 text-sm text-foreground-500">{manga.subtitle}</p>
              <p className="mt-3 text-sm leading-6 text-foreground-700">{manga.synopsis}</p>

              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-lg bg-background-50 p-3 text-center">
                  <p className="font-heading text-lg text-primary-600">{manga.rating.toFixed(1)}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">评分</p>
                </div>
                <div className="rounded-lg bg-background-50 p-3 text-center">
                  <p className="font-heading text-lg text-primary-600">{manga.chapters}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">章回</p>
                </div>
                <div className="rounded-lg bg-background-50 p-3 text-center">
                  <p className="font-heading text-lg text-primary-600">{manga.views}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">阅读</p>
                </div>
                <div className="rounded-lg bg-background-50 p-3 text-center">
                  <p className="font-heading text-lg text-primary-600">{manga.year}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">连载年</p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1"><i className="ri-user-line text-primary-500"></i>{manga.author}</span>
                <span className="flex items-center gap-1"><i className="ri-bookmark-line text-primary-500"></i>最新章节：{manga.latestChapter}</span>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                {manga.tags.map((t) => (
                  <span key={t} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">{t}</span>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <Link to="#reader" onClick={() => selectChapter(0)} className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600">
                  <i className="ri-play-circle-line"></i>开始阅读
                </Link>
                <button className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-2.5 text-sm text-foreground-700 hover:bg-background-200">
                  <i className="ri-star-line"></i>收藏
                </button>
                <button className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-2.5 text-sm text-foreground-700 hover:bg-background-200">
                  <i className="ri-share-line"></i>分享
                </button>
              </div>
            </div>
          </header>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <aside className="rounded-lg border border-background-200 bg-background-50 p-4 lg:sticky lg:top-24 lg:self-start">
              <h2 className="font-heading text-lg text-foreground-950">章节目录</h2>
              <p className="mt-1 text-xs text-foreground-500">共 {chapterList.length} 话 · 点击切换章节</p>
              <ul className="mt-4 grid max-h-96 grid-cols-1 gap-2 overflow-y-auto pr-1">
                {chapterList.map((c, i) => (
                  <li key={c.num}>
                    <button
                      onClick={() => selectChapter(i)}
                      className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
                        i === chapterIdx ? "bg-primary-500 font-medium text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      <span className="truncate">第{c.num}话 {c.title}</span>
                      <span className={`ml-2 shrink-0 text-xs ${i === chapterIdx ? "text-background-100" : "text-foreground-400"}`}>{c.date.slice(5)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            <section id="reader" className="lg:col-span-2">
              <MangaReader manga={manga} chapterIdx={chapterIdx} onChangeChapter={selectChapter} chapters={chapterList} />
            </section>
          </div>

          <section className="mt-12">
            <div className="flex items-end justify-between">
              <div>
                <h2 className="font-heading text-2xl text-foreground-950">相关漫画推荐</h2>
                <p className="mt-2 text-sm text-foreground-600">你可能还喜欢这些作品</p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {related.map((m) => (
                <Link
                  key={m.id}
                  to={`/article/${m.slug}`}
                  className="group block overflow-hidden rounded-lg border border-background-200 bg-background-50 hover:border-primary-200"
                >
                  <div className="aspect-[3/4] w-full overflow-hidden bg-background-100">
                    <img src={m.cover} alt={`${m.title} 漫画封面`} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-3">
                    <p className="truncate text-sm font-semibold text-foreground-950 group-hover:text-primary-600">{m.title}</p>
                    <p className="mt-1 text-xs text-foreground-500">{m.chapters}话 · {m.rating.toFixed(1)}分</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  if (news) {
    const jsonLd = [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: news.title,
        url: `https://swimchats.github.io/article/${news.slug}`,
        image: news.cover,
        description: news.summary,
        datePublished: news.date,
        author: { "@type": "Organization", name: "178漫画网" },
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://swimchats.github.io/" },
          { "@type": "ListItem", position: 2, name: "漫画资讯", item: "https://swimchats.github.io/" },
          { "@type": "ListItem", position: 3, name: news.title },
        ],
      },
    ];

    return (
      <div className="min-h-screen bg-background-50">
        <Seo
          title={`${news.title}-178漫画网`}
          description={news.summary}
          keywords={news.tags.join(",")}
          canonical={`https://swimchats.github.io/article/${news.slug}`}
          ogImage={news.cover}
          jsonLd={jsonLd}
        />
        <Navbar />
        <main className="mx-auto max-w-7xl px-4 md:px-6 pt-24 md:pt-28 pb-14">
          <div className="pt-4">
            <Breadcrumb items={[{ label: "漫画资讯", to: "/" }, { label: news.title }]} />
          </div>
          <header className="mt-6 mb-8">
            <h1 className="font-heading text-2xl leading-snug text-foreground-950 md:text-3xl">{news.title}</h1>
          </header>
          <NewsReader article={news} />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 pt-32 pb-16 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-400"></i>
        <h1 className="font-heading text-2xl text-foreground-950">内容未找到</h1>
        <p className="text-sm text-foreground-600">你访问的内容不存在或已被移动。</p>
        <Link to="/" className="mt-2 rounded-full bg-primary-500 px-6 py-2.5 text-sm text-background-50 hover:bg-primary-600">返回首页</Link>
      </main>
      <Footer />
    </div>
  );
}